<template>
  <div class="card">
    <div class="card-header staff-list-header">
      <div class="name-search">
        <input
          type="text"
          v-model="staffName"
          class="form-control form-control-sm"
          placeholder="名前で検索"
          :disabled="sortMode"
          @keyup.enter="searchStaff"
        />
        <button class="btn btn-sm btn-outline-secondary" :disabled="sortMode" @click="searchStaff">
          <i class="fas fa-search"></i>
        </button>
      </div>
      <div class="header-right">
        <label class="archive-toggle">
          <input type="checkbox" v-model="showArchived" :disabled="sortMode || fetching" />
          <span>アーカイブ済みを表示</span>
        </label>
        <button
          :class="['btn btn-sm', sortMode ? 'btn-warning' : 'btn-outline-secondary']"
          :disabled="showArchived || fetching"
          @click="toggleSortMode"
        >
          <i class="fas fa-sort mr-1"></i>{{ sortMode ? '並び替え完了' : '並び替え' }}
        </button>
        <button class="btn btn-success" @click="this.$router.push({name:'StoreStaffRegisterPage'});">
          <i class="fas fa-plus mr-1"></i>スタッフを登録
        </button>
      </div>
    </div>
    <div class="card-body p-0">
      <div class="table-responsive">
        <table class="table table-hover staff-table">
          <thead>
            <tr>
              <th v-if="sortMode" style="width:70px;">順番</th>
              <th>名前</th>
              <th>勤務パターン</th>
              <th class="text-center" style="width:100px;">時給</th>
              <th class="text-center" style="width:110px;">日当交通費</th>
              <th class="text-center" style="width:80px;">詳細</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(staff, idx) in data.staff" :key="staff.id"
              :draggable="sortMode"
              :class="{ 'drag-row': sortMode, 'drag-over': sortMode && dragOverIdx === idx, 'row-archived': staff.deleted_at }"
              @dragstart="onDragStart(idx, $event)"
              @dragover.prevent="onDragOver(idx)"
              @dragleave="onDragLeave"
              @drop="onDrop(idx)"
              @dragend="onDragEnd"
            >
              <td v-if="sortMode" class="td-sort">
                <i class="fas fa-grip-vertical drag-handle"></i>
              </td>
              <td class="td-name">
                {{ staff.name }}
                <span v-if="staff.deleted_at" class="badge-archived">アーカイブ済み</span>
              </td>
              <td class="td-patterns">
                <div v-if="staff.work_patterns && staff.work_patterns.length > 0" class="patterns-list">
                  <div v-for="p in staff.work_patterns" :key="p.id" class="pattern-chip">
                    <span class="pattern-chip-name">{{ p.pattern_name }}</span>
                    <span class="pattern-chip-time">{{ formatTime(p.start_time) }}〜{{ formatTime(p.end_time) }}</span>
                    <span class="pattern-chip-break">休{{ p.break_time }}分</span>
                    <span class="pattern-chip-days">
                      <span v-for="d in dayDefs" :key="d.key" :class="['mini-day', { active: p[d.key], [d.cls]: true }]">{{ d.label }}</span>
                    </span>
                  </div>
                </div>
                <span v-else class="text-muted">{{ staff.regular_working_hours_start }} - {{ staff.regular_working_hours_stop }}（休{{ staff.break_time }}分）</span>
              </td>
              <td class="text-center">{{ yen(staff.hourly_wage) }}</td>
              <td class="text-center">{{ yen(staff.daily_transportation_allowance) }}</td>
              <td class="text-center">
                <!-- アーカイブ済みスタッフは StaffController::show() が withTrashed() を
                     持たず 404 になるため、詳細への導線を出さない -->
                <button
                  v-if="!staff.deleted_at"
                  class="btn btn-sm btn-outline-primary"
                  @click="viewDetails(staff.id)"
                >
                  <i class="fas fa-arrow-right"></i>
                </button>
              </td>
            </tr>
            <tr v-if="!data.staff || data.staff.length === 0">
              <td :colspan="sortMode ? 6 : 5" class="text-center text-muted py-4">スタッフデータがありません</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data () {
    return {
      data: [],
      staffName: null,
      showArchived: false,
      // 取得中はチェックボックスと並び替えボタンの両方をロックする。
      // showArchived を切り替えた直後、応答が届く前に並び替えを始められると、
      // 画面に残った古い配列（削除済みスタッフを含む）のまま sort_order を
      // 保存してしまうため。
      fetching: false,
      sortMode: false,
      dragIdx: null,
      dragOverIdx: null,
      dayDefs: [
        { key: 'monday', label: '月', cls: '' },
        { key: 'tuesday', label: '火', cls: '' },
        { key: 'wednesday', label: '水', cls: '' },
        { key: 'thursday', label: '木', cls: '' },
        { key: 'friday', label: '金', cls: '' },
        { key: 'saturday', label: '土', cls: 'sat' },
        { key: 'sunday', label: '日', cls: 'sun' },
      ],
    }
  },
  watch: {
    showArchived() {
      // 検索中に切り替えたら検索結果のほうを取り直す
      this.staffName ? this.searchStaff() : this.getStaffList();
    },
  },
  mounted() {
    this.getStaffList();
  },
  methods: {
    getStaffList () {
      const shop_id = this.$store.getters.shop;
      this.fetching = true;
      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/staffList/'+shop_id, {
        params: this.showArchived ? { with_trashed: 1 } : {}
      })
      .then(response => {
        if (response.data.success) {
          this.data = response.data.data[0];
        }
      })
      .catch(error => {
        if (error.response) {
          this.$toast.error(error.response.data.message);
        }
      })
      .then(() => {
        this.fetching = false;
      });
    },
    formatTime(t) {
      return t ? t.slice(0, 5) : '';
    },
    // 金額を桁区切り付きで表示する。null / undefined は「—」。0 は「0円」。
    // != null にしているのは、バックエンド未デプロイでキーが undefined のときに
    // NaN円 を描画しないため。
    yen(amount) {
      return amount != null ? Number(amount).toLocaleString() + '円' : '—';
    },
    viewDetails (staff_id) {
      this.$router.push({name:'StoreStaffDetailsPage', params: {staff_id: staff_id}});
    },
    toggleSortMode() {
      if (this.sortMode) {
        this.saveSortOrder();
      }
      this.sortMode = !this.sortMode;
    },
    onDragStart(idx, e) {
      this.dragIdx = idx;
      e.dataTransfer.effectAllowed = 'move';
    },
    onDragOver(idx) {
      this.dragOverIdx = idx;
    },
    onDragLeave() {
      this.dragOverIdx = null;
    },
    onDrop(idx) {
      if (this.dragIdx === null || this.dragIdx === idx) return;
      const list = [...this.data.staff];
      const item = list.splice(this.dragIdx, 1)[0];
      list.splice(idx, 0, item);
      this.data = { ...this.data, staff: list };
      this.dragIdx = null;
      this.dragOverIdx = null;
    },
    onDragEnd() {
      this.dragIdx = null;
      this.dragOverIdx = null;
    },
    saveSortOrder() {
      const items = this.data.staff.map((s, i) => ({ id: s.id, sort_order: i }));
      this.$http.post(process.env.VUE_APP_API_PATH + '/shop/staff/sort-order', { items })
      .then(() => {
        this.$toast.success('並び順を保存しました');
      })
      .catch(() => {
        this.$toast.error('並び順の保存に失敗しました');
      });
    },
    searchStaff () {
      const companyId = this.$store.getters.company;
      const shopId = this.$store.getters.shop;
      if (this.staffName) {
        this.fetching = true;
        this.$http.get(process.env.VUE_APP_API_PATH + '/shop/staffSearch/'+companyId+'/'+shopId+'/'+this.staffName, {
          params: this.showArchived ? { with_trashed: 1 } : {}
        })
        .then(response => {
          if (response.data.success) {
            this.data.staff = response.data.data;
          }
        })
        .catch(error => {
          if (error.response) {
            this.$toast.error(error.response.data.message);
          }
        })
        .then(() => {
          this.fetching = false;
        });
      } else {
        this.getStaffList();
      }
    }
  },
};
</script>

<style scoped>
.staff-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f7fafc;
}

.name-search {
  display: flex;
  gap: 4px;
}

.name-search .form-control {
  width: 200px;
}

.staff-table thead th {
  font-size: 13px;
  font-weight: 700;
  color: #4a5568;
  background: #f7fafc;
  border-bottom: 2px solid #e2e8f0;
  padding: 14px 16px;
  white-space: nowrap;
}

.staff-table tbody td {
  padding: 12px 16px;
  font-size: 15px;
  vertical-align: middle;
  border-color: #edf2f7;
}

.staff-table tbody tr {
  transition: background-color 0.15s ease;
}

.staff-table tbody tr:hover {
  background-color: #f0faf4;
}

.td-name {
  font-weight: 600;
  color: #1a202c;
  white-space: nowrap;
}

.td-patterns {
  min-width: 200px;
}

.patterns-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pattern-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  background: #f0fff4;
  border: 1px solid #c6f6d5;
  border-radius: 6px;
  font-size: 13px;
}

.pattern-chip-name {
  font-weight: 700;
  color: #22543d;
  min-width: 40px;
}

.pattern-chip-time {
  color: #049C4C;
  font-weight: 600;
  white-space: nowrap;
}

.pattern-chip-break {
  color: #718096;
  font-size: 12px;
  white-space: nowrap;
}

.pattern-chip-days {
  display: flex;
  gap: 2px;
  margin-left: auto;
}

.mini-day {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  background: #e2e8f0;
  color: #a0aec0;
}

.mini-day.active {
  background: #049C4C;
  color: #fff;
}

.mini-day.sat.active {
  background: #3182ce;
}

.mini-day.sun.active {
  background: #e53e3e;
}

.header-right {
  display: flex;
  gap: 8px;
}

.td-sort {
  white-space: nowrap;
  text-align: center;
  width: 40px;
}

.drag-handle {
  color: #a0aec0;
  font-size: 16px;
  cursor: grab;
}

.drag-row {
  cursor: grab;
}

.drag-row:active {
  cursor: grabbing;
}

.drag-over {
  border-top: 3px solid #049C4C !important;
}

.drag-row[draggable="true"] {
  user-select: none;
}

.archive-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  cursor: pointer;
  white-space: nowrap;
}

.archive-toggle input {
  width: 15px;
  height: 15px;
  cursor: pointer;
}

.badge-archived {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: #fed7d7;
  color: #822727;
  vertical-align: middle;
}

.row-archived {
  background-color: #fffafa;
}

.row-archived .td-name {
  color: #a0aec0;
}
</style>
